import React, { useEffect, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import rrwebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';
import pako from 'pako';
import './AgreementPlaybackDialog.less';

const normalizeUrl = (url) => {
  if (!url) return '';
  return url.trim().replace(/^http:\/\//, 'https://');
};

const buildCandidateUrls = (url) => {
  const normalized = normalizeUrl(url);
  if (!normalized) return [];

  const candidates = [normalized];
  if (normalized.includes('.cos.ap-nanjing.myqcloud.com')) {
    const isProdCos = normalized.includes('litta-prd-');
    const cdnDomain = isProdCos ? 'prd-cos.litta.cn' : 'test-cos.litta.cn';
    const cdnUrl = normalized.replace(/^https:\/\/[^/]+/, `https://${cdnDomain}`);
    if (!candidates.includes(cdnUrl)) {
      candidates.push(cdnUrl);
    }
  }

  return candidates;
};

const normalizeMutationData = (data = {}) => ({
  ...data,
  adds: Array.isArray(data.adds) ? data.adds : [],
  texts: Array.isArray(data.texts) ? data.texts : [],
  attributes: Array.isArray(data.attributes) ? data.attributes : [],
  removes: Array.isArray(data.removes) ? data.removes : [],
});

const normalizeReplayEvent = (event) => {
  if (!event || typeof event !== 'object') return null;

  const normalizedEvent = { ...event };
  const eventData = normalizedEvent.data;

  if (!eventData || typeof eventData !== 'object') {
    return normalizedEvent;
  }

  switch (eventData.source) {
    case 0:
      normalizedEvent.data = normalizeMutationData(eventData);
      break;
    case 1:
    case 6:
    case 12:
      normalizedEvent.data = {
        ...eventData,
        positions: Array.isArray(eventData.positions) ? eventData.positions : [],
      };
      break;
    case 8:
      normalizedEvent.data = {
        ...eventData,
        adds: Array.isArray(eventData.adds) ? eventData.adds : [],
        removes: Array.isArray(eventData.removes) ? eventData.removes : [],
      };
      break;
    case 9:
      normalizedEvent.data = {
        ...eventData,
        commands: Array.isArray(eventData.commands) ? eventData.commands : [],
      };
      break;
    default:
      break;
  }

  return normalizedEvent;
};

const normalizeReplayEvents = (events) => {
  if (!Array.isArray(events)) return [];
  return events.map((event) => normalizeReplayEvent(event)).filter(Boolean);
};

const parseTraceBuffer = (buffer) => {
  const uint8 = new Uint8Array(buffer);

  // 尝试 pako 解压
  try {
    const decompressed = pako.inflate(uint8, { to: 'string' });
    return JSON.parse(decompressed);
  } catch (inflateError) {
    // 尝试直接解析 JSON
    try {
      const text = new TextDecoder('utf-8').decode(uint8);
      const parsedJson = JSON.parse(text);

      if (Array.isArray(parsedJson)) {
        return parsedJson;
      }

      if (Array.isArray(parsedJson?.rrwebEvents)) {
        return parsedJson.rrwebEvents;
      }

      if (typeof parsedJson?.rrwebEvents === 'string') {
        const decoded = window.atob(parsedJson.rrwebEvents);
        const bytes = new Uint8Array(decoded.length);
        for (let i = 0; i < decoded.length; i += 1) {
          bytes[i] = decoded.charCodeAt(i);
        }
        const decompressed = pako.inflate(bytes, { to: 'string' });
        return JSON.parse(decompressed);
      }
    } catch (jsonError) {
      console.error('解析回放文件失败:', jsonError);
    }
  }

  throw new Error('无法解析该回放文件，请确认链接有效且文件格式正确');
};

const AgreementPlaybackDialog = ({
  visible,
  onClose,
  sourceUrl,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const playerRef = useRef(null);
  const playerInstanceRef = useRef(null);
  const latestRequestIdRef = useRef(0);
  const playerTextObserverRef = useRef(null);
  const pendingEventsRef = useRef(null);

  const destroyPlayer = () => {
    if (playerTextObserverRef.current) {
      playerTextObserverRef.current.disconnect();
      playerTextObserverRef.current = null;
    }

    if (playerInstanceRef.current?.$destroy) {
      playerInstanceRef.current.$destroy();
    }
    playerInstanceRef.current = null;

    if (playerRef.current) {
      playerRef.current.innerHTML = '';
    }
  };

  const localizePlayerTexts = () => {
    if (!playerRef.current) return;

    const textMappings = [
      {
        pattern: /skip inactive/gi,
        replacement: '跳过无操作时段',
      },
    ];

    const walker = document.createTreeWalker(playerRef.current, NodeFilter.SHOW_TEXT);

    let currentNode = walker.nextNode();
    while (currentNode) {
      const originalText = currentNode.textContent || '';
      let localizedText = originalText;

      textMappings.forEach(({ pattern, replacement }) => {
        localizedText = localizedText.replace(pattern, replacement);
      });

      if (localizedText !== originalText) {
        currentNode.textContent = localizedText;
      }

      currentNode = walker.nextNode();
    }
  };

  const observePlayerTexts = () => {
    if (!playerRef.current) return;

    if (playerTextObserverRef.current) {
      playerTextObserverRef.current.disconnect();
    }

    playerTextObserverRef.current = new MutationObserver(() => {
      localizePlayerTexts();
    });

    playerTextObserverRef.current.observe(playerRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    localizePlayerTexts();
  };

  const mountPlayer = (events) => {
    pendingEventsRef.current = events;
  };

  const doMountPlayer = async (events) => {
    destroyPlayer();

    const width = Math.min(window.innerWidth - 96, 360);
    const height = Math.min(window.innerHeight - 180, 640);

    playerInstanceRef.current = new rrwebPlayer({
      target: playerRef.current,
      props: {
        events,
        autoPlay: true,
        width: Math.max(width, 300),
        height: Math.max(height, 520),
        showController: false,
      },
    });

    observePlayerTexts();
  };

  const loadPlayback = async () => {
    if (!sourceUrl) {
      setErrorMessage('暂无可播放地址');
      return;
    }

    const requestId = Date.now();
    latestRequestIdRef.current = requestId;
    setLoading(true);
    setErrorMessage('');

    try {
      const candidates = buildCandidateUrls(sourceUrl);
      let lastError = null;

      for (const url of candidates) {
        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`网络请求失败，HTTP ${response.status}`);
          }

          const buffer = await response.arrayBuffer();
          const events = normalizeReplayEvents(parseTraceBuffer(buffer));

          if (!Array.isArray(events) || !events.length) {
            throw new Error('回放事件为空');
          }

          if (latestRequestIdRef.current !== requestId) {
            return;
          }

          mountPlayer(events);
          return;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error('播放文件加载失败');
    } catch (error) {
      console.error('加载签署记录回放失败:', error);
      setErrorMessage(error?.message || '回放文件加载失败');
      destroyPlayer();
    } finally {
      if (latestRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  // 在 loading 结束后挂载播放器，确保 DOM 稳定
  useEffect(() => {
    if (!loading && pendingEventsRef.current && playerRef.current && visible) {
  
      doMountPlayer(pendingEventsRef.current);
      pendingEventsRef.current = null;
    }
  }, [loading, visible]);

  useEffect(() => {
    if (visible && sourceUrl) {
      loadPlayback();
    } else if (!visible) {
      setErrorMessage('');
      setLoading(false);
      destroyPlayer();
    }
  }, [visible, sourceUrl]);

  useEffect(() => {
    return () => {
      destroyPlayer();
    };
  }, []);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title="签署记录回放"
      width={400}
      footer={null}
      centered
      destroyOnClose
      className="agreement-playback-dialog"
    >
      <Spin spinning={loading}>
        <div className="preview-container">
          <div className="playback-panel">
            {errorMessage && (
              <div className="playback-state playback-state--error">
                <div className="state-title">播放失败</div>
                <div className="state-desc">{errorMessage}</div>
              </div>
            )}
            {!sourceUrl && !errorMessage && (
              <div className="playback-state playback-state--empty">
                <div className="state-title">暂无回放地址</div>
                <div className="state-desc">请先传入有效的签署记录回放文件地址</div>
              </div>
            )}
            <div ref={playerRef} className="player-host" />
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default AgreementPlaybackDialog;
